import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import {
  Button,
  ButtonProps,
  LoadingDots,
} from "components/DesignSystem/Button/Button";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { ArrowDown } from "components/icons/ArrowDown";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { SmallSparkle } from "components/icons/SmallSparkle";
import { Padlock } from "components/icons/Padlock";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { Table } from "react-table-8.10.7";
import {
  useChangeTransactionStatusMutation,
  useLazyGetTransactionsFilterSetQuery,
  useUpdateMultipleTransactionsDataMutation,
} from "store/apis/transactions";
import { useBulkConfirmAIClassificationsMutation } from "store/apis/classifications";
import { setSelectedTransactionIds } from "store/slices/transactions";
import { Transactions, TXN_ACCOUNT, TxnAccountType } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { BulkAssignVendor } from "./BulkAssignVendor";
import { BulkCategorise } from "./BulkCategorise";
import { BulkClassification } from "./BulkClassification";
import { UncategoriseModal } from "./UncategoriseModal";
import { SmartReview } from "./SmartReview/SmartReview";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useWindowSize } from "hooks/useWindowSize";
import { MobileSelectionActions } from "./MobileSelectionActions";

type TableSelectionControlsProps = {
  txnAccountType?: TxnAccountType;
  table: Table<Transactions>;
  sendToChat: ({
    transactionIds,
  }: {
    transactionIds: string[];
  }) => Promise<void>;
  onRequestInfo: ({ transactionId }: { transactionId?: string }) => void;
  deselectRows: () => void;
  unSelectedRowsIds: string[];
  isAllTxnSelected: boolean;
  selectionCount: number;
  allSelectedRows: { uuid: string; category_id?: string }[];
};

export const TableSelectionControls = ({
  table,
  sendToChat,
  onRequestInfo,
  deselectRows,
  txnAccountType,
  isAllTxnSelected,
  unSelectedRowsIds,
  selectionCount,
  allSelectedRows,
}: TableSelectionControlsProps) => {
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const entityId = useCurrentEntityId();
  const { isAdmin, isCpa, isForeignCA } = useRoleBasedView();
  const { isMobile } = useWindowSize();
  const { uuid: groupId } = useCurrentGroupContext();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const {
    filters: {
      uncategorised,
      minAmount,
      maxAmount,
      excludedTransactions,
      ...restFilter
    },
  } = useAppSelector((state) => state.transactionFilter);

  const [changeTransactionStatus, { isLoading }] =
    useChangeTransactionStatusMutation();
  const [getTxnFilterSet, { isLoading: isFilterSetLoading }] =
    useLazyGetTransactionsFilterSetQuery();
  const [updateMultipleTransactionsData, { isLoading: isUpdating }] =
    useUpdateMultipleTransactionsDataMutation();
  const [bulkConfirmAIClassifications, { isLoading: isConfirmingAI }] =
    useBulkConfirmAIClassificationsMutation();

  const buttonProps = {
    size: "small",
  } as ButtonProps;

  const transactionsSelected = allSelectedRows.length > 0;
  const isConfirmingCategory = isUpdating || isFilterSetLoading;

  const transactionIds = useMemo(
    () => allSelectedRows.map(({ uuid }) => uuid),
    [allSelectedRows]
  );

  // Only offer "Apply AI suggested classes" when at least one selected txn has a pending suggestion.
  const hasAISuggestionInSelection = useMemo(
    () =>
      table
        .getSelectedRowModel()
        .rows.some((row) => row.original.has_suggestions),
    [table, allSelectedRows]
  );

  const bulkCategoriseModal = useModal();
  const bulkUncategoriseModal = useModal();
  const bulkAssignVendorModal = useModal();
  const bulkClassificationModal = useModal();
  const subscriptionModal = useModal();
  const smartReviewModal = useModal();

  const onBulkTransactionsCategorise = () => {
    bulkCategoriseModal.open();
    dispatch(setSelectedTransactionIds(transactionIds));
  };

  const onBulkUncategorise = () => {
    bulkUncategoriseModal.open();
    dispatch(setSelectedTransactionIds(transactionIds));
  };

  const onBulkAssignVendor = () => {
    bulkAssignVendorModal.open();
    dispatch(setSelectedTransactionIds(transactionIds));
  };

  const onBulkClassification = () => {
    bulkClassificationModal.open();
    dispatch(setSelectedTransactionIds(transactionIds));
  };

  const onApplyAllAIClassifications = async () => {
    try {
      await bulkConfirmAIClassifications({
        entityId,
        payload: { transaction_ids: transactionIds },
      }).unwrap();
      const pluralizedTransaction = pluralize(
        selectionCount,
        "transaction",
        "transactions"
      );
      successToast({
        message: `Applied AI classifications for ${pluralizedTransaction}`,
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const getHashKey = async () => {
    try {
      return await getTxnFilterSet({
        groupId,
        entityId,
        uncategorized: uncategorised,
        txnStatus: excludedTransactions?.value
          ? ("EXCLUDED" as const)
          : undefined,
        minAmount: ((minAmount?.value || "") as string)
          ?.replaceAll("$", "")
          .replaceAll(",", ""),
        maxAmount: ((maxAmount?.value || "") as string)
          ?.replaceAll("$", "")
          .replaceAll(",", ""),
        filter_operation: "EXCLUDE",
        excluded_txns_list: unSelectedRowsIds,
        ...restFilter,
      }).unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onBulkChangeStatus = async ({
    transactionStatus,
  }: {
    transactionStatus: "EXCLUDED" | "RESTORED";
  }) => {
    try {
      if (isAllTxnSelected) {
        const res = await getHashKey();
        await changeTransactionStatus({
          entityId,
          payload: {
            transaction_status: transactionStatus,
            transactions_hash_key: res?.txns_hash_key,
          },
        }).unwrap();
      } else {
        await changeTransactionStatus({
          entityId,
          payload: {
            transaction_ids: transactionIds,
            transaction_status: transactionStatus,
          },
        }).unwrap();
      }
      const pluralizedTransaction = pluralize(
        selectionCount,
        "transaction",
        "transactions"
      );

      successToast({
        message:
          transactionStatus === "EXCLUDED"
            ? `${pluralizedTransaction} excluded`
            : `${pluralizedTransaction} restored`,
      });
      dispatch(setSelectedTransactionIds([]));
      deselectRows();
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as {}
      );
    }
  };

  const onBulkConfirmCategory = async () => {
    try {
      if (isAllTxnSelected) {
        const res = await getHashKey();
        await updateMultipleTransactionsData({
          groupId,
          entityId,
          ai_category_confirmation: true,
          transactions_hash_key: res?.txns_hash_key,
          transactions: [],
        }).unwrap();
      } else {
        await updateMultipleTransactionsData({
          groupId,
          entityId,
          transactions: allSelectedRows,
          ai_category_confirmation: true,
        }).unwrap();
      }
      const pluralizedTransaction = pluralize(
        selectionCount,
        "transaction",
        "transactions"
      );
      successToast({
        message: `Confirmed categories for ${pluralizedTransaction}`,
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const mobileActions = (
    <MobileSelectionActions
      isExcludedView={Boolean(excludedTransactions?.value)}
      isAllTxnSelected={isAllTxnSelected}
      isStripe={txnAccountType === TXN_ACCOUNT.STRIPE}
      hasWritePermission={hasWritePermission}
      canRequestInfo={isAdmin || isCpa || isForeignCA}
      hasAISuggestion={hasAISuggestionInSelection}
      isBusy={isLoading || isFilterSetLoading || isConfirmingCategory}
      onCategorise={onBulkTransactionsCategorise}
      onUncategorise={onBulkUncategorise}
      onConfirmCategory={onBulkConfirmCategory}
      onSmartReview={smartReviewModal.open}
      onSendToChat={() => sendToChat({ transactionIds })}
      onRequestInfo={() => onRequestInfo({})}
      onClassification={onBulkClassification}
      onApplyAIClassifications={onApplyAllAIClassifications}
      onUpgrade={subscriptionModal.open}
      onAssignVendor={onBulkAssignVendor}
      onExclude={() => onBulkChangeStatus({ transactionStatus: "EXCLUDED" })}
      onRestore={() => onBulkChangeStatus({ transactionStatus: "RESTORED" })}
    />
  );

  return (
    <>
      {isMobile && mobileActions}
      {!isMobile && (
        <motion.span
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ease: "easeInOut", duration: 0.2 }}
          key={transactionsSelected.toString()}
          className="t-flex t-gap-3 t-items-center"
        >
          {excludedTransactions?.value ? (
            <ConditionalToolTip
              condition={!hasWritePermission && "You have read-only access"}
            >
              <div>
                <Button
                  {...buttonProps}
                  onClick={() =>
                    onBulkChangeStatus({
                      transactionStatus: "RESTORED",
                    })
                  }
                  isLoading={isLoading || isFilterSetLoading}
                  disabled={
                    isLoading || isFilterSetLoading || !hasWritePermission
                  }
                >
                  {!hasWritePermission && (
                    <span className="t-mr-1 t-inline-flex">
                      <Padlock showTooltip={false} />
                    </span>
                  )}
                  Restore
                </Button>
              </div>
            </ConditionalToolTip>
          ) : (
            <>
              <ConditionalToolTip
                condition={!hasWritePermission && "You have read-only access"}
              >
                <div>
                  <Dropdown.Root modal={false}>
                    <Dropdown.Trigger
                      className="t-group"
                      asChild
                      disabled={
                        txnAccountType === TXN_ACCOUNT.STRIPE ||
                        !hasWritePermission
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          disabled={
                            txnAccountType === TXN_ACCOUNT.STRIPE ||
                            !hasWritePermission
                          }
                        >
                          {!hasWritePermission && (
                            <span className="t-mr-1 t-inline-flex">
                              <Padlock showTooltip={false} />
                            </span>
                          )}
                          Categorisation
                          <span className="group-data-state-open:t-rotate-180 t-transition-transform">
                            <ArrowDown />
                          </span>
                        </Button>
                      </span>
                    </Dropdown.Trigger>
                    <Dropdown.Content align="start" sideOffset={8}>
                      <Dropdown.Item
                        onSelect={(e) => {
                          e.preventDefault();
                          onBulkTransactionsCategorise();
                        }}
                      >
                        Categorise
                      </Dropdown.Item>
                      <Dropdown.Item
                        onSelect={(e) => {
                          e.preventDefault();
                          onBulkUncategorise();
                        }}
                      >
                        Uncategorise
                      </Dropdown.Item>
                      <Dropdown.Item
                        disabled={isConfirmingCategory}
                        onSelect={(e) => {
                          e.preventDefault();
                          onBulkConfirmCategory();
                        }}
                        className="t-flex t-items-center t-gap-2"
                      >
                        <span>Confirm category</span>
                        {isConfirmingCategory && <LoadingDots />}
                      </Dropdown.Item>
                      <Dropdown.Item
                        onSelect={(e) => {
                          e.preventDefault();
                          smartReviewModal.open();
                        }}
                        className="t-flex t-items-center  t-gap-2"
                      >
                        <span>Smart review</span>
                        <Tag tagType="blue" size="small" rounded icon={false}>
                          New
                        </Tag>
                      </Dropdown.Item>
                    </Dropdown.Content>
                  </Dropdown.Root>
                </div>
              </ConditionalToolTip>
              {!isAllTxnSelected && (
                <>
                  <Button
                    {...buttonProps}
                    onClick={() =>
                      sendToChat({
                        transactionIds,
                      })
                    }
                  >
                    Send to chat
                  </Button>
                  {(isAdmin || isCpa || isForeignCA) && (
                    <Button {...buttonProps} onClick={() => onRequestInfo({})}>
                      Request info
                    </Button>
                  )}
                  <ConditionalToolTip
                    condition={
                      !hasWritePermission && "You have read-only access"
                    }
                  >
                    <div>
                      <PermissionBasedUI
                        feature={CLASSIFICATION_FEATURE_ACCESS}
                        onUpgrade={() => subscriptionModal.open()}
                        blockedUI={
                          <div className="t-flex t-items-center t-gap-1">
                            <Button
                              {...buttonProps}
                              disabled={!hasWritePermission}
                            >
                              {!hasWritePermission && (
                                <span className="t-mr-1 t-inline-flex">
                                  <Padlock showTooltip={false} />
                                </span>
                              )}
                              Classification
                              <span className="t-pl-1">
                                <DiamondPurple height={13} width={14} />
                              </span>
                            </Button>
                          </div>
                        }
                      >
                        {hasAISuggestionInSelection ? (
                          <Dropdown.Root modal={false}>
                            <Dropdown.Trigger
                              className="t-group"
                              asChild
                              disabled={!hasWritePermission}
                            >
                              <span>
                                <Button
                                  size="small"
                                  disabled={
                                    !hasWritePermission || isConfirmingAI
                                  }
                                >
                                  {!hasWritePermission && (
                                    <span className="t-mr-1 t-inline-flex">
                                      <Padlock showTooltip={false} />
                                    </span>
                                  )}
                                  Classification
                                  <span className="group-data-state-open:t-rotate-180 t-transition-transform">
                                    <ArrowDown />
                                  </span>
                                </Button>
                              </span>
                            </Dropdown.Trigger>
                            <Dropdown.Content align="start" sideOffset={8}>
                              <Dropdown.Item
                                onSelect={(e) => {
                                  e.preventDefault();
                                  onBulkClassification();
                                }}
                              >
                                Apply Classification
                              </Dropdown.Item>
                              <Dropdown.Item
                                onSelect={() => onApplyAllAIClassifications()}
                                className="t-flex t-items-center t-gap-2"
                              >
                                <span className="t-flex t-items-center t-text-purple">
                                  <SmallSparkle width={16} height={16} />
                                </span>
                                <span>Apply AI suggested classes</span>
                              </Dropdown.Item>
                            </Dropdown.Content>
                          </Dropdown.Root>
                        ) : (
                          // No suggestion in selection — plain button opens the modal directly (old behaviour).
                          <Button
                            size="small"
                            disabled={!hasWritePermission}
                            onClick={onBulkClassification}
                          >
                            {!hasWritePermission && (
                              <span className="t-mr-1 t-inline-flex">
                                <Padlock showTooltip={false} />
                              </span>
                            )}
                            Classification
                          </Button>
                        )}
                      </PermissionBasedUI>
                    </div>
                  </ConditionalToolTip>
                </>
              )}

              <ConditionalToolTip
                condition={!hasWritePermission && "You have read-only access"}
              >
                <div>
                  <Button
                    {...buttonProps}
                    disabled={!hasWritePermission}
                    onClick={onBulkAssignVendor}
                  >
                    {!hasWritePermission && (
                      <span className="t-mr-1 t-inline-flex">
                        <Padlock showTooltip={false} />
                      </span>
                    )}
                    Assign vendor
                  </Button>
                </div>
              </ConditionalToolTip>

              <ConditionalToolTip
                condition={!hasWritePermission && "You have read-only access"}
              >
                <div>
                  <Button
                    {...buttonProps}
                    onClick={() =>
                      onBulkChangeStatus({
                        transactionStatus: "EXCLUDED",
                      })
                    }
                    isLoading={isLoading || isFilterSetLoading}
                    disabled={
                      !hasWritePermission || isLoading || isFilterSetLoading
                    }
                  >
                    {!hasWritePermission && (
                      <span className="t-mr-1 t-inline-flex">
                        <Padlock showTooltip={false} />
                      </span>
                    )}
                    Exclude
                  </Button>
                </div>
              </ConditionalToolTip>
            </>
          )}
        </motion.span>
      )}

      {bulkCategoriseModal.isOpen && (
        <BulkCategorise
          deselectRows={deselectRows}
          close={bulkCategoriseModal.close}
          isOpen={bulkCategoriseModal.isOpen}
          unSelectedRowsIds={unSelectedRowsIds}
          isAllTxnSelected={isAllTxnSelected}
        />
      )}

      {bulkUncategoriseModal.isOpen && (
        <UncategoriseModal
          deselectRows={deselectRows}
          close={bulkUncategoriseModal.close}
          isOpen={bulkUncategoriseModal.isOpen}
          unSelectedRowsIds={unSelectedRowsIds}
          isAllTxnSelected={isAllTxnSelected}
        />
      )}
      {bulkAssignVendorModal.isOpen && (
        <BulkAssignVendor
          deselectRows={deselectRows}
          close={bulkAssignVendorModal.close}
          isOpen={bulkAssignVendorModal.isOpen}
          unSelectedRowsIds={unSelectedRowsIds}
          isAllTxnSelected={isAllTxnSelected}
        />
      )}
      {bulkClassificationModal.isOpen && (
        <BulkClassification
          deselectRows={deselectRows}
          close={bulkClassificationModal.close}
          isOpen={bulkClassificationModal.isOpen}
          allSelectedRows={allSelectedRows}
        />
      )}
      <BooksSoftwareSubscriptionsModal
        open={subscriptionModal.isOpen}
        onClose={() => subscriptionModal.close()}
        entityId={entityId}
        onProductSubscribe={() => subscriptionModal.close()}
        modalDescription={
          <ModalDescriptionWrapper value="Unlock Classifications and more with Books Standard Plan" />
        }
      />
      <SmartReview
        isOpen={smartReviewModal.isOpen}
        close={smartReviewModal.close}
      />
    </>
  );
};
